/**
 * SwarmSkillEffect — Conjuration Fork 1: multi-wisp saturation.
 *
 * Swarm replaces single-wisp invocation with a multi-wisp burst. On activation,
 * spawns up to `maxWisps` wisps simultaneously (3 by default), each at a
 * distinct angular offset from the player's facing direction to prevent
 * pixel-stacking. Existing living wisps are not replaced — only the delta
 * between current count and the cap is spawned.
 *
 * Mechanic:
 * - Query `summons.getWispCount()` to get current living wisp count.
 * - Spawn `maxWisps - currentCount` new wisps (0 if already at cap).
 * - At cap: activate() returns false immediately (cooldown not consumed).
 * - Each new wisp is placed at an evenly-distributed angular offset across
 *   a 120° arc centered on the player's facing direction, preventing stacking.
 *
 * Wisp stats match Invoke-tier: 22 HP, 80% aggro weight, A* tracking AI.
 * Inherits Outstretched and Tenacious bonuses via 'summon' resolver scope.
 *
 * UpgradeEffectResolver key(s):
 * - (swarm scope) maxWisps: max, default 3 — Saturation increases to 4
 * - (summon scope) spawnDistanceTiles: additive — from Outstretched
 * - (summon scope) driftSpeedMultiplier: multiplicative — from Outstretched
 * - (summon scope) hpMultiplier: multiplicative — from Tenacious
 * - (summon scope) aggroRedirectChance: max — from Tenacious
 *
 * Medium cooldown tier (1200ms). Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see conjuration-spec.md § Swarm
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type { WispSummonConfig } from './SummonSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface SwarmDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string, base?: number): number;
    getMaxEffect(skillId: string, key: string): number | undefined;
  };
  readonly player: {
    getPosition(): { x: number; y: number };
    getFacingDirection(): { x: number; y: number };
  };
  readonly collision: {
    isWallAt(x: number, y: number): boolean;
  };
  readonly summons: {
    spawn(config: WispSummonConfig, x: number, y: number): string;
    /** Returns the number of currently living wisps managed by SummonSystem. */
    getWispCount(): number;
  };
}

// ---------------------------------------------------------------------------
// Skill tuning
// ---------------------------------------------------------------------------

export interface SwarmSkillConfig {
  readonly cooldownMs: number;
  /** Maximum living wisps before Swarm stops spawning new ones. */
  readonly maxWisps: number;
  /** Invoke-tier wisp HP baseline (22 per spec). */
  readonly baseHp: number;
  /** Invoke-tier aggro weight (80% redirect). */
  readonly baseAggroWeight: number;
  /** Base spawn distance in tiles from the player position. */
  readonly baseSpawnDistanceTiles: number;
  /** Normalized base drift speed. */
  readonly baseDriftSpeed: number;
  /** Maximum follow distance before wisps return toward player (tiles). */
  readonly leashRadiusTiles: number;
  /** Tile size in pixels — used for spawn position calculation. */
  readonly tileSize: number;
  /** Total arc in degrees across which N wisps are spread. */
  readonly arcRangeDeg: number;
}

const DEFAULT_CONFIG: SwarmSkillConfig = {
  cooldownMs: 1200,              // medium cooldown tier
  maxWisps: 3,                   // baseline per spec (design doc value)
  baseHp: 22,                    // Invoke-tier wisp HP
  baseAggroWeight: 0.8,          // 80% aggro redirect at Invoke/Swarm tier
  baseSpawnDistanceTiles: 1,     // base 1 tile ahead (Outstretched adds +1)
  baseDriftSpeed: 1.0,           // integration layer applies px/s
  leashRadiusTiles: 8,
  tileSize: 32,
  arcRangeDeg: 120,              // total spread arc for multi-wisp fan
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class SwarmSkillEffect implements SkillEffect {
  readonly skillId = 'swarm';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: SwarmDeps;
  private readonly config: SwarmSkillConfig;

  private _cooldownRemaining = 0;

  // Observable state for scenarios and integration layer
  private _totalSpawns = 0;
  private _lastActivationSpawnCount = 0;
  private _lastSpawnPositions: Array<{ x: number; y: number }> = [];
  private _lastSpawnIds: string[] = [];
  private _resolvedHp = 0;
  private _resolvedAggroWeight = 0;
  private _resolvedDriftSpeed = 0;
  private _resolvedSpawnDistanceTiles = 0;
  private _resolvedMaxWisps = 0;

  constructor(deps: SwarmDeps, config: Partial<SwarmSkillConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get totalSpawns(): number { return this._totalSpawns; }
  get lastActivationSpawnCount(): number { return this._lastActivationSpawnCount; }
  get lastSpawnPositions(): ReadonlyArray<{ x: number; y: number }> { return this._lastSpawnPositions; }
  get lastSpawnIds(): readonly string[] { return this._lastSpawnIds; }
  get resolvedHp(): number { return this._resolvedHp; }
  get resolvedAggroWeight(): number { return this._resolvedAggroWeight; }
  get resolvedDriftSpeed(): number { return this._resolvedDriftSpeed; }
  get resolvedSpawnDistanceTiles(): number { return this._resolvedSpawnDistanceTiles; }
  get resolvedMaxWisps(): number { return this._resolvedMaxWisps; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Resolve max wisp cap: Saturation contributes 4 via max aggregation
    const maxWispsOverride = this.deps.resolver.getMaxEffect('swarm', 'maxWisps');
    const maxWisps = maxWispsOverride !== undefined
      ? Math.max(this.config.maxWisps, maxWispsOverride)
      : this.config.maxWisps;
    this._resolvedMaxWisps = maxWisps;

    const currentCount = this.deps.summons.getWispCount();
    const toSpawn = Math.max(0, maxWisps - currentCount);

    if (toSpawn === 0) {
      // Already at cap — signal failure without consuming cooldown.
      // Player can retry as soon as a wisp dies.
      this._lastActivationSpawnCount = 0;
      return false;
    }

    const pos = this.deps.player.getPosition();
    const facing = this.deps.player.getFacingDirection();

    // Resolve shared upgrade bonuses via 'summon' scope (Outstretched, Tenacious)
    const spawnDistanceBonus = this.deps.resolver.getAdditiveEffect('summon', 'spawnDistanceTiles');
    const distanceTiles = this.config.baseSpawnDistanceTiles + spawnDistanceBonus;
    this._resolvedSpawnDistanceTiles = distanceTiles;

    const hpMultiplier = this.deps.resolver.getMultiplicativeEffect('summon', 'hpMultiplier', 1.0);
    const resolvedHp = Math.round(this.config.baseHp * hpMultiplier);
    this._resolvedHp = resolvedHp;

    const aggroOverride = this.deps.resolver.getMaxEffect('summon', 'aggroRedirectChance');
    const resolvedAggroWeight = aggroOverride !== undefined
      ? Math.max(this.config.baseAggroWeight, aggroOverride)
      : this.config.baseAggroWeight;
    this._resolvedAggroWeight = resolvedAggroWeight;

    const driftMultiplier = this.deps.resolver.getMultiplicativeEffect('summon', 'driftSpeedMultiplier', 1.0);
    const resolvedDriftSpeed = this.config.baseDriftSpeed * driftMultiplier;
    this._resolvedDriftSpeed = resolvedDriftSpeed;

    // Spread wisps evenly across a 120° arc centered on the facing direction.
    // For N wisps: angular offsets span from -arcRange/2 to +arcRange/2 in N steps.
    // Special case N=1: spawn straight ahead at 0° offset.
    const arcRangeRad = (this.config.arcRangeDeg * Math.PI) / 180;
    const spawnIds: string[] = [];
    const spawnPositions: Array<{ x: number; y: number }> = [];

    for (let i = 0; i < toSpawn; i++) {
      const angleRad = toSpawn > 1
        ? (i / (toSpawn - 1) - 0.5) * arcRangeRad
        : 0;

      const cosA = Math.cos(angleRad);
      const sinA = Math.sin(angleRad);
      const dirX = facing.x * cosA - facing.y * sinA;
      const dirY = facing.x * sinA + facing.y * cosA;

      const spawnX = pos.x + dirX * distanceTiles * this.config.tileSize;
      const spawnY = pos.y + dirY * distanceTiles * this.config.tileSize;

      let finalX = spawnX;
      let finalY = spawnY;
      if (this.deps.collision.isWallAt(spawnX, spawnY)) {
        finalX = pos.x;
        finalY = pos.y;
      }

      const wispConfig: WispSummonConfig = {
        id: `swarm_wisp_${this._totalSpawns + i}`,
        archetype: 'wisp',
        baseHp: resolvedHp,
        baseDamage: 0,
        baseSpeed: resolvedDriftSpeed,
        leashRadiusTiles: this.config.leashRadiusTiles,
        aggroWeight: resolvedAggroWeight,
        aiMode: 'tracking',
      };

      spawnIds.push(this.deps.summons.spawn(wispConfig, finalX, finalY));
      spawnPositions.push({ x: finalX, y: finalY });
    }

    this._lastActivationSpawnCount = toSpawn;
    this._lastSpawnPositions = spawnPositions;
    this._lastSpawnIds = spawnIds;
    this._totalSpawns += toSpawn;
    this._cooldownRemaining = this.config.cooldownMs;

    return true;
  }

  deactivate(): void {
    // Instant skill — no toggle state
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
    this._lastSpawnPositions = [];
    this._lastSpawnIds = [];
  }
}
